import {
  type FormEvent,
  type ReactNode,
  useEffect,
  useState,
  useSyncExternalStore,
} from "react";
import { useLogin, useLogout, useNotify, useRefresh } from "react-admin";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { ApiError, authStore, changePassword, loadCurrentUser } from "./client";
import { validateNewPassword } from "./authValidation";

export function useBackofficeAuth() {
  return useSyncExternalStore(
    authStore.subscribe,
    authStore.getSnapshot,
    authStore.getSnapshot,
  );
}

function fieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError)) return {};
  return error.violations.reduce<Record<string, string>>(
    (fields, violation) => {
      const field = violation.field.split(".").pop() || violation.field;
      fields[field] = fields[field]
        ? `${fields[field]} ${violation.message}`
        : violation.message;
      return fields;
    },
    {},
  );
}

export function ApiErrorNotice({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <Alert severity="error" sx={{ overflowWrap: "anywhere" }}>
      {error instanceof Error
        ? error.message
        : "Something went wrong. Try again."}
      {error instanceof ApiError && error.requestId && (
        <Typography
          component="div"
          variant="caption"
          sx={{ mt: 0.75, fontFamily: "monospace", opacity: 0.8 }}
        >
          Request ID: {error.requestId}
        </Typography>
      )}
    </Alert>
  );
}

function AccessFrame({
  children,
  title,
  description,
}: {
  children: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <Box
      component="main"
      sx={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        bgcolor: "background.default",
        backgroundImage:
          "radial-gradient(ellipse at 50% 0%, rgba(124,58,237,0.13), transparent 60%)",
        px: 2,
        py: 5,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 440 }}>
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Box
            sx={{
              display: "grid",
              placeItems: "center",
              width: 38,
              height: 38,
              border: "1px solid rgba(167,139,250,0.4)",
              bgcolor: "rgba(124,58,237,0.1)",
              color: "primary.light",
              borderRadius: 1,
            }}
          >
            <ShieldOutlinedIcon fontSize="small" />
          </Box>
          <Box>
            <Typography
              sx={{ fontWeight: 800, letterSpacing: 2, fontSize: 14 }}
            >
              ALGOL
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Backoffice
            </Typography>
          </Box>
        </Stack>
        <Paper
          variant="outlined"
          sx={{ p: { xs: 3, sm: 4 }, borderColor: "divider" }}
        >
          <Typography
            component="h1"
            variant="h5"
            sx={{ fontWeight: 650, mb: 1 }}
          >
            {title}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.65 }}>
            {description}
          </Typography>
          {children}
        </Paper>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", textAlign: "center", mt: 3 }}
        >
          Authorized personnel only
        </Typography>
      </Box>
    </Box>
  );
}

function PasswordField({
  id,
  label,
  value,
  onChange,
  error,
  helperText,
  autoComplete,
  disabled,
  autoFocus,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  helperText?: string;
  autoComplete: string;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <TextField
      id={id}
      name={id}
      label={label}
      type={visible ? "text" : "password"}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      required
      fullWidth
      error={Boolean(error)}
      helperText={error || helperText}
      autoComplete={autoComplete}
      disabled={disabled}
      autoFocus={autoFocus}
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={`${visible ? "Hide" : "Show"} ${label.toLowerCase()}`}
                aria-pressed={visible}
                onClick={() => setVisible(!visible)}
                edge="end"
                disabled={disabled}
                size="small"
              >
                {visible ? (
                  <VisibilityOffOutlinedIcon fontSize="small" />
                ) : (
                  <VisibilityOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

export function LoginPage() {
  const login = useLogin();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const errors = fieldErrors(error);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending) return;
    setError(null);
    setPending(true);
    try {
      await login({ email: email.trim(), password });
    } catch (cause) {
      setError(cause);
      setPending(false);
    }
  }

  return (
    <AccessFrame
      title="Sign in to Backoffice"
      description="Manage players, games and operations from one workspace."
    >
      <Stack component="form" onSubmit={submit} spacing={2.5}>
        <ApiErrorNotice error={error} />
        <TextField
          id="email"
          name="email"
          type="email"
          label="Email address"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="username"
          autoFocus
          required
          fullWidth
          disabled={pending}
          error={Boolean(errors.email)}
          helperText={errors.email}
        />
        <PasswordField
          id="password"
          label="Password"
          value={password}
          onChange={setPassword}
          error={errors.password}
          autoComplete="current-password"
          disabled={pending}
        />
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={pending || !email.trim() || !password}
          endIcon={
            pending ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <ArrowForwardOutlinedIcon />
            )
          }
          sx={{ minHeight: 44 }}
        >
          {pending ? "Signing in…" : "Sign in"}
        </Button>
      </Stack>
    </AccessFrame>
  );
}

export function ChangePasswordPage({
  required = false,
}: {
  required?: boolean;
}) {
  const auth = useBackofficeAuth();
  const logout = useLogout();
  const navigate = useNavigate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [validation, setValidation] = useState<Record<string, string>>({});
  const errors = { ...fieldErrors(error), ...validation };
  const forced = required || auth.passwordChangeRequired;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending) return;
    const errors = validateNewPassword(
      currentPassword,
      newPassword,
      confirmation,
    );
    setValidation(errors);
    setError(null);
    if (Object.keys(errors).length) return;
    setPending(true);
    try {
      await changePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmation("");
      notify("Your password has been changed.", { type: "success" });
      refresh();
      navigate("/", { replace: true });
    } catch (cause) {
      setError(cause);
    } finally {
      setPending(false);
    }
  }

  const form = (
    <Stack component="form" onSubmit={submit} spacing={2.5}>
      {forced && (
        <Alert severity="info" icon={<LockOutlinedIcon fontSize="small" />}>
          Change your temporary password to access Backoffice.
        </Alert>
      )}
      <ApiErrorNotice error={error} />
      <PasswordField
        id="currentPassword"
        label="Current password"
        value={currentPassword}
        onChange={setCurrentPassword}
        error={errors.currentPassword}
        autoComplete="current-password"
        autoFocus
        disabled={pending}
      />
      <PasswordField
        id="newPassword"
        label="New password"
        value={newPassword}
        onChange={setNewPassword}
        error={errors.newPassword}
        helperText="12–72 UTF-8 bytes. Most English characters use one byte."
        autoComplete="new-password"
        disabled={pending}
      />
      <PasswordField
        id="confirmation"
        label="Confirm new password"
        value={confirmation}
        onChange={setConfirmation}
        error={errors.confirmation}
        autoComplete="new-password"
        disabled={pending}
      />
      <Button
        type="submit"
        variant="contained"
        size="large"
        disabled={pending}
        endIcon={
          pending ? <CircularProgress size={16} color="inherit" /> : undefined
        }
      >
        {pending ? "Updating password…" : "Update password"}
      </Button>
      {forced ? (
        <>
          <Divider />
          <Button
            color="inherit"
            onClick={() => void logout()}
            disabled={pending}
          >
            Sign out
          </Button>
        </>
      ) : (
        <Button
          color="inherit"
          onClick={() => navigate("/")}
          disabled={pending}
        >
          Cancel
        </Button>
      )}
    </Stack>
  );

  if (forced) {
    return (
      <AccessFrame
        title="Set your new password"
        description="Your account requires a password update before you continue."
      >
        {form}
      </AccessFrame>
    );
  }
  return (
    <Box sx={{ maxWidth: 560, mx: "auto", px: 2, py: 4 }}>
      <Typography component="h1" variant="h5" sx={{ mb: 1, fontWeight: 650 }}>
        Change password
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Update the password you use to sign in to Backoffice.
      </Typography>
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        {form}
      </Paper>
    </Box>
  );
}

export function SessionGate({ children }: { children: ReactNode }) {
  const auth = useBackofficeAuth();
  const { pathname, search } = useLocation();
  const logout = useLogout();
  useEffect(() => {
    if (
      auth.authenticated &&
      !auth.passwordChangeRequired &&
      !auth.user &&
      !auth.identityLoading &&
      !auth.identityError
    ) {
      void loadCurrentUser().catch(() => undefined);
    }
  }, [
    auth.authenticated,
    auth.passwordChangeRequired,
    auth.user,
    auth.identityLoading,
    auth.identityError,
  ]);

  if (!auth.authenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ nextPathname: pathname, nextSearch: search }}
      />
    );
  }
  if (auth.passwordChangeRequired) return <ChangePasswordPage required />;
  if (!auth.user) {
    return (
      <AccessFrame
        title={
          auth.identityError
            ? "We couldn’t load your account"
            : "Loading your workspace"
        }
        description={
          auth.identityError
            ? "Your account details must be verified before you can continue."
            : "Verifying your account and permissions…"
        }
      >
        <Stack spacing={2}>
          {auth.identityLoading || !auth.identityError ? (
            <CircularProgress
              size={26}
              aria-label="Loading account"
              sx={{ alignSelf: "center" }}
            />
          ) : (
            <>
              <ApiErrorNotice error={auth.identityError} />
              <Alert severity="info">
                <AlertTitle>Access is paused</AlertTitle>
                Retry when the connection is available.
              </Alert>
              <Button
                variant="contained"
                onClick={() => void loadCurrentUser().catch(() => undefined)}
              >
                Retry
              </Button>
            </>
          )}
          <Button color="inherit" onClick={() => void logout()}>
            Sign out
          </Button>
        </Stack>
      </AccessFrame>
    );
  }
  return children;
}
